import { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { useAuth } from '@clerk/clerk-react';
import { useAtom } from 'jotai';
import { authAtom, jotaiStore } from '~/atoms/authAtom';

interface FeedbackDialogProps {
  visible: boolean;
  onHide: () => void;
}

export const FeedbackDialog: React.FC<FeedbackDialogProps> = ({
  visible,
  onHide,
}) => {
  const [feedback, setFeedback] = useState('');
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);
  const auth = useAuth();

  const MAX_FEEDBACK_LENGTH = 1000;

  // Reset feedback text when dialog opens
  useEffect(() => {
    if (visible) {
      setFeedback('');
    }
  }, [visible]);

  const handleFeedbackSubmit = async () => {
    if (!feedback.trim()) return;

    try {
      setIsSubmittingFeedback(true);

      // Use the authAtom to get token with organization context via banqora_template
      const authState = jotaiStore.get(authAtom);
      const token = await authState?.auth.getToken({
        template: 'banqora_template',
      });

      if (!token) {
        throw new Error('Failed to get authentication token');
      }

      // Use direct API call since the current endpoint interface doesn't match the API spec
      const response = await fetch(
        `${process.env.REACT_BANQORA_PORTFOLIO_ANALYSIS_API_URL}api/v1/portfolio-analysis/feedback`,
        {
          method: 'POST',
          headers: {
            accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message: feedback,
            userId: auth.userId,
            orgId: auth.orgId,
          }),
        },
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          `HTTP error! status: ${response.status} - ${errorText}`,
        );
      }

      const result = await response.json();
      console.log('Feedback submitted successfully:', result);

      // Clear the feedback and close the dialog
      setFeedback('');
      onHide();
    } catch (error) {
      console.error('Failed to submit feedback:', error);
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const dialogFooter = (
    <div className="flex justify-content-end align-items-center gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={onHide}
        severity="secondary"
        disabled={isSubmittingFeedback}
      />
      <Button
        label="Submit Feedback"
        icon={isSubmittingFeedback ? 'pi pi-spin pi-spinner' : 'pi pi-send'}
        onClick={handleFeedbackSubmit}
        loading={isSubmittingFeedback}
        disabled={
          !feedback.trim() ||
          isSubmittingFeedback ||
          feedback.length > MAX_FEEDBACK_LENGTH
        }
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header="Send Feedback"
      visible={visible}
      onHide={onHide}
      style={{ width: '40vw' }}
      className="p-fluid"
      footer={dialogFooter}
      pt={{
        header: {
          style: {
            backgroundColor: 'var(--bq-surface-elevated)',
            color: 'var(--bq-text-primary)',
            padding: '1rem 1.5rem',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
          },
        },
        content: {
          style: {
            backgroundColor: 'var(--bq-surface-elevated)',
            color: 'var(--bq-text-primary)',
          },
        },
        footer: {
          style: {
            backgroundColor: 'var(--bq-surface-elevated)',
            borderTop: '1px solid var(--bq-border-muted)',
            padding: '1rem 1.5rem',
            minHeight: '60px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
          },
        },
      }}
    >
      <div className="dark-form">
        <div
          style={{
            height: '1px',
            backgroundColor: 'var(--bq-border-muted)',
            margin: '0 -1.5rem',
            width: 'calc(100% + 3rem)',
          }}
        />

        <div className="grid pt-3">
          <div className="col-12">
            <div className="text-sm text-600 mb-3">
              We value your feedback! Please share your thoughts about the pilot
              experience to help us improve.
            </div>
          </div>

          <div className="col-12">
            <div className="field">
              <label htmlFor="feedback" className="font-bold block mb-2">
                Your Feedback
              </label>
              <InputTextarea
                id="feedback"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Share your thoughts about the pilot experience..."
                rows={4}
                className="w-full"
                style={{ resize: 'none' }}
                maxLength={MAX_FEEDBACK_LENGTH}
              />
              <div className="flex justify-content-between align-items-center mt-2">
                <small
                  className={`text-xs ${feedback.length > MAX_FEEDBACK_LENGTH ? 'text-red-400' : 'text-500'}`}
                >
                  {feedback.length > MAX_FEEDBACK_LENGTH
                    ? `Character limit exceeded (${feedback.length}/${MAX_FEEDBACK_LENGTH})`
                    : `${feedback.length}/${MAX_FEEDBACK_LENGTH} characters`}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
